import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { Conversation, Message } from '../../types/conversation';
import { formatRecentMessageTime } from '../../utils/format';
import { GROUP_CHAT_AVATAR_SRC } from '../../utils/groupAvatar';
import {
  createWeComScrollViewContext,
  disposeWeComOpenDataFrame,
  mountWeComOpenDataFrame,
  openWeComPreviewModal,
  triggerWWOpenDataBind,
} from '../../services/wecomSDK';
import { toWeComConversationDisplayMessage } from './wecomConversationDisplay';
import {
  getWeComConversationFallbackName,
  getWeComConversationNameType,
  getWeComConversationOpenDataId,
  getWeComExternalCorpDisplay,
  stripWeComExternalCorpSuffix,
} from './wecomConversationIdentity';

type OpenDataFrameInstance = {
  dispose?: () => void;
  destroy?: () => void;
  setData?: (data: unknown) => void;
};

type ScrollViewContextInstance = {
  scrollTo?: (options: { top?: number; left?: number }) => void;
};

export type InheritRelatedConversation = {
  id: string;
  type: 'single' | 'group';
  name: string;
  avatar?: string;
  employee_name?: string;
  employee_wecom_userid?: string;
  message_count: number;
  today_count: number;
  last_message: string;
  last_message_time?: string;
  wecom_room_id?: string;
  group_member_count?: number;
};

type FrameConversation = {
  id: string;
  rowClass: string;
  openid: string;
  name: string;
  nameType: 'userName' | 'chatName';
  avatarType: 'userAvatar';
  showAvatarOpenData: boolean;
  avatarImage: string;
  avatarInitial: string;
  countText: string;
  lastMessageTime: string;
};

type FrameData = {
  mode: 'list' | 'detail';
  loading: boolean;
  listLoading: boolean;
  listRefreshing: boolean;
  messagesLoading: boolean;
  contextReady: boolean;
  contextError: string;
  activeTab: 'single' | 'group';
  keyword: string;
  searchPlaceholder: string;
  headerOpenId: string;
  headerNameType: 'userName' | 'externalUserName' | 'chatName';
  headerAvatarType: 'userAvatar' | 'externalUserAvatar';
  headerShowAvatarOpenData: boolean;
  headerAvatarImage: string;
  headerAvatarInitial: string;
  headerName: string;
  headerCompany: string;
  canRefresh: boolean;
  conversations: FrameConversation[];
  selectedName: string;
  selectedOpenId: string;
  selectedNameType: 'userName' | 'chatName';
  selectedUseOpenData: boolean;
  selectedGroupOwnerOpenId: string;
  selectedGroupOwnerName: string;
  selectedGroupMemberText: string;
  selectedShowGroupSummary: boolean;
  canGoBack: boolean;
  messages: ReturnType<typeof toWeComConversationDisplayMessage>[];
  hasMore: boolean;
  messageError: string;
};

type Props = {
  conversation: Conversation | null;
  conversations: InheritRelatedConversation[];
  selectedConversation: InheritRelatedConversation | null;
  activeTab: 'single' | 'group';
  keyword: string;
  loading: boolean;
  listLoading: boolean;
  listRefreshing: boolean;
  messagesLoading: boolean;
  contextError: string;
  messageError: string;
  messages: Message[];
  hasMore: boolean;
  onRefresh: () => void;
  onTabChange: (type: 'single' | 'group') => void;
  onKeywordChange: (value: string) => void;
  onConversationSelect: (id: string) => void;
  onBack: () => void;
  onLoadMore: () => void;
  onRetryMessages: () => void;
};

const template = `
  <view class="inherit-frame">
    <view wx:if="{{data.mode === 'list'}}" class="inherit-list-page">
      <view class="inherit-list-header">
        <view class="inherit-contact-row">
          <view class="inherit-avatar inherit-contact-avatar">
            <ww-open-data wx:if="{{data.headerShowAvatarOpenData}}" type="{{data.headerAvatarType}}" openid="{{data.headerOpenId}}" class="inherit-avatar-open">{{data.headerAvatarInitial}}</ww-open-data>
            <image wx:elif="{{data.headerAvatarImage}}" src="{{data.headerAvatarImage}}" class="inherit-avatar-image" mode="aspectFill"></image>
            <view wx:else class="inherit-avatar-fallback">{{data.headerAvatarInitial}}</view>
          </view>
          <view class="inherit-contact-main">
            <view class="inherit-contact-name">
              <ww-open-data wx:if="{{data.contextReady && data.headerOpenId}}" type="{{data.headerNameType}}" openid="{{data.headerOpenId}}">{{data.headerName}}</ww-open-data>
              <span wx:else>{{data.headerName}}</span>
            </view>
            <view wx:if="{{data.headerCompany}}" class="inherit-contact-company">{{data.headerCompany}}</view>
          </view>
          <view class="{{data.canRefresh ? 'inherit-refresh' : 'inherit-refresh inherit-disabled'}}" bindclick="onRefresh">
            <span class="{{data.listRefreshing ? 'inherit-refresh-icon inherit-spinning' : 'inherit-refresh-icon'}}">↻</span>
          </view>
        </view>
        <view class="inherit-toolbar">
          <view class="{{data.activeTab === 'single' ? 'inherit-tab inherit-tab-active' : 'inherit-tab'}}" data-type="single" bindclick="onTabChange">单聊</view>
          <view class="{{data.activeTab === 'group' ? 'inherit-tab inherit-tab-active' : 'inherit-tab'}}" data-type="group" bindclick="onTabChange">群聊</view>
          <view class="inherit-search">
            <span class="inherit-search-icon">⌕</span>
            <input class="inherit-search-input" value="{{data.keyword}}" placeholder="{{data.searchPlaceholder}}" bindinput="onSearchInput" />
          </view>
        </view>
      </view>
      <scroll-view class="inherit-conversation-list" scroll-y="{{true}}" show-scrollbar="{{true}}">
        <view wx:if="{{data.loading || data.listLoading}}" class="inherit-status inherit-status-upper">
          <view class="inherit-spinner"></view>
        </view>
        <view wx:elif="{{data.contextError}}" class="inherit-status inherit-status-upper">
          <view class="inherit-toast"><view class="inherit-toast-icon">!</view><view>未获取到客户<br/>信息</view></view>
        </view>
        <view wx:elif="{{data.conversations.length === 0}}" class="inherit-empty">暂无数据</view>
        <view wx:for="{{data.conversations}}" wx:key="id" class="{{item.rowClass}}" data-id="{{item.id}}" bindclick="onConversationClick">
          <view class="inherit-avatar inherit-row-avatar" data-id="{{item.id}}">
            <ww-open-data wx:if="{{item.showAvatarOpenData}}" type="{{item.avatarType}}" openid="{{item.openid}}" class="inherit-avatar-open">{{item.avatarInitial}}</ww-open-data>
            <image wx:elif="{{item.avatarImage}}" src="{{item.avatarImage}}" class="inherit-avatar-image" mode="aspectFill"></image>
            <view wx:else class="inherit-avatar-fallback">{{item.avatarInitial}}</view>
          </view>
          <view class="inherit-row-main" data-id="{{item.id}}">
            <view class="inherit-row-name" data-id="{{item.id}}">
              <ww-open-data wx:if="{{item.openid}}" type="{{item.nameType}}" openid="{{item.openid}}">{{item.name}}</ww-open-data>
              <span wx:else>{{item.name}}</span>
            </view>
            <view class="inherit-row-count" data-id="{{item.id}}">{{item.countText}}</view>
          </view>
          <view class="inherit-row-time" data-id="{{item.id}}">{{item.lastMessageTime}}</view>
        </view>
      </scroll-view>
    </view>

    <view wx:else class="inherit-detail-page">
      <view class="inherit-detail-header">
        <view wx:if="{{data.canGoBack}}" class="inherit-back" bindclick="onBack">‹</view>
        <view class="inherit-detail-title">
          <ww-open-data wx:if="{{data.selectedUseOpenData}}" type="{{data.selectedNameType}}" openid="{{data.selectedOpenId}}">{{data.selectedName}}</ww-open-data>
          <span wx:else>{{data.selectedName}}</span>
          <view wx:if="{{data.selectedShowGroupSummary}}" class="inherit-group-summary">
            <span wx:if="{{data.selectedGroupOwnerOpenId || data.selectedGroupOwnerName}}">群主: </span>
            <ww-open-data wx:if="{{data.selectedGroupOwnerOpenId}}" type="userName" openid="{{data.selectedGroupOwnerOpenId}}">{{data.selectedGroupOwnerName}}</ww-open-data>
            <span wx:elif="{{data.selectedGroupOwnerName}}">{{data.selectedGroupOwnerName}}</span>
            <span wx:if="{{(data.selectedGroupOwnerOpenId || data.selectedGroupOwnerName) && data.selectedGroupMemberText}}"> | </span>
            <span wx:if="{{data.selectedGroupMemberText}}">{{data.selectedGroupMemberText}}</span>
          </view>
        </view>
      </view>
      <view class="inherit-detail-filters">
        <view class="inherit-search inherit-message-search">
          <span class="inherit-search-icon">⌕</span>
          <input class="inherit-search-input" value="{{data.keyword}}" placeholder="聊天记录" bindinput="onSearchInput" />
        </view>
      </view>
      <scroll-view ref="messageScroll" class="inherit-message-list" scroll-y="{{true}}" show-scrollbar="{{true}}" upper-threshold="24" bindscrolltoupper="onLoadMore">
        <view wx:if="{{data.messageError}}" class="inherit-message-error">
          <span>{{data.messageError}}</span>
          <view class="inherit-retry" bindclick="onRetryMessages">重试</view>
        </view>
        <view wx:if="{{data.hasMore}}" class="inherit-load-more" bindclick="onLoadMore">{{data.messagesLoading ? '加载中...' : '加载更多历史消息'}}</view>
        <view wx:if="{{data.messagesLoading && data.messages.length === 0}}" class="inherit-status inherit-status-upper"><view class="inherit-spinner"></view></view>
        <view wx:elif="{{data.messages.length === 0 && !data.messageError}}" class="inherit-empty">暂无消息</view>
        <view wx:for="{{data.messages}}" wx:key="key" class="inherit-message-row">
          <view class="inherit-message-sender">
            <ww-open-data wx:if="{{item.senderOpenId}}" type="{{item.senderNameType}}" openid="{{item.senderOpenId}}">{{item.senderFallback}}</ww-open-data>
            <span wx:else>{{item.senderFallback}}</span>
            <span class="inherit-message-time">{{item.sendTime}}</span>
          </view>
          <view class="inherit-message-content">
            <ww-suspense wx:if="{{item.native}}">
              <ww-media-preview-group wx:if="{{item.previewable}}">
                <ww-open-message class="inherit-open-message" message-id="{{item.msgid}}" secret-key="{{item.secretKey}}" open-type="viewMessage" display-type="{{item.displayType}}" binderror="onOpenMessageError" />
              </ww-media-preview-group>
              <ww-open-message wx:else class="inherit-open-message" message-id="{{item.msgid}}" secret-key="{{item.secretKey}}" open-type="viewMessage" display-type="{{item.displayType}}" binderror="onOpenMessageError" />
              <block wx:slot="fallback"><view class="inherit-open-message-fallback">消息内容正在加载...</view></block>
            </ww-suspense>
            <view wx:else class="inherit-message-fallback">{{item.fallback}}</view>
          </view>
        </view>
      </scroll-view>
    </view>
  </view>
`;

const style = `
  .inherit-frame { width: 100%; height: 100%; min-width: 0; overflow: hidden; background: #fff; color: #20242a; font-family: "PingFang SC", "Microsoft YaHei", sans-serif; }
  .inherit-list-page, .inherit-detail-page { width: 100%; height: 100%; min-width: 0; display: flex; flex-direction: column; overflow: hidden; }
  .inherit-list-header { flex-shrink: 0; padding: 12px 16px 10px; border-bottom: 1px solid #d6d9de; box-sizing: border-box; background: #fff; }
  .inherit-contact-row { min-width: 0; min-height: 44px; display: flex; align-items: center; }
  .inherit-avatar { flex-shrink: 0; overflow: hidden; background: #d6d6d6; }
  .inherit-contact-avatar, .inherit-row-avatar { width: 44px; height: 44px; border-radius: 6px; }
  .inherit-avatar-open, .inherit-avatar-image, .inherit-avatar-fallback { display: flex; width: 100%; height: 100%; align-items: center; justify-content: center; box-sizing: border-box; object-fit: cover; }
  .inherit-avatar-fallback { color: #fff; background: #2868c7; font-size: 16px; }
  .inherit-contact-main { min-width: 0; flex: 1; margin-left: 10px; overflow: hidden; }
  .inherit-contact-name { overflow: hidden; color: #0a355f; font-size: 14px; line-height: 20px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
  .inherit-contact-company { overflow: hidden; margin-top: 1px; color: #2b983f; font-size: 13px; line-height: 18px; text-overflow: ellipsis; white-space: nowrap; }
  .inherit-refresh { flex-shrink: 0; margin-left: 8px; display: flex; align-items: center; gap: 5px; color: #0b4c89; font-size: 14px; line-height: 20px; }
  .inherit-refresh-icon { font-size: 22px; line-height: 22px; }
  .inherit-disabled { opacity: .38; }
  .inherit-toolbar { min-width: 0; height: 34px; margin-top: 11px; display: flex; align-items: center; gap: 6px; }
  .inherit-tab { width: 50px; height: 32px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 5px; color: #20242a; font-size: 14px; line-height: 20px; white-space: nowrap; }
  .inherit-tab-active { color: #4c7ef3; background: #eef3ff; font-weight: 600; }
  .inherit-search { min-width: 0; height: 32px; flex: 1; margin-left: 6px; display: flex; align-items: center; border: 1px solid #cbd0d8; border-radius: 6px; box-sizing: border-box; background: #fff; }
  .inherit-search-icon { flex-shrink: 0; margin: 0 7px 0 10px; color: #68717d; font-size: 23px; line-height: 24px; transform: rotate(-20deg); }
  .inherit-search-input { min-width: 0; height: 30px; flex: 1; padding: 0 8px 0 0; border: 0; outline: 0; background: transparent; color: #20242a; font-size: 14px; line-height: 30px; box-sizing: border-box; }
  .inherit-conversation-list { display: block; min-height: 0; flex: 1; background: #fff; overflow-x: hidden; }
  .inherit-conversation-row { min-width: 0; padding: 12px 16px; display: flex; align-items: center; gap: 12px; border-bottom: 1px solid #dce7f3; box-sizing: border-box; }
  .inherit-row-main { min-width: 0; flex: 1; overflow: hidden; }
  .inherit-row-name { overflow: hidden; color: #0a355f; font-size: 14px; line-height: 20px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
  .inherit-row-count { overflow: hidden; margin-top: 2px; color: #36577a; font-size: 13px; line-height: 18px; text-overflow: ellipsis; white-space: nowrap; }
  .inherit-row-time { flex-shrink: 0; color: #36577a; font-size: 13px; line-height: 18px; white-space: nowrap; }
  .inherit-status { width: 100%; display: flex; justify-content: center; box-sizing: border-box; }
  .inherit-status-upper { min-height: 180px; padding-top: 72px; align-items: flex-start; }
  .inherit-spinner { width: 20px; height: 20px; border: 2px solid #d6e1ee; border-top-color: #3478c9; border-radius: 50%; animation: inherit-spin .8s linear infinite; box-sizing: border-box; }
  .inherit-toast { width: 112px; min-height: 112px; display: flex; flex-direction: column; align-items: center; justify-content: center; border-radius: 7px; background: rgba(32,32,32,.82); color: #fff; text-align: center; font-size: 13px; line-height: 19px; }
  .inherit-toast-icon { margin-bottom: 7px; font-size: 24px; line-height: 24px; }
  .inherit-empty { padding: 72px 16px 20px; color: #8a919c; font-size: 13px; line-height: 20px; text-align: center; }
  .inherit-detail-header { min-height: 52px; flex-shrink: 0; display: grid; grid-template-columns: 40px minmax(0,1fr) 40px; align-items: center; padding: 0 8px; box-sizing: border-box; background: #fff; }
  .inherit-back { color: #20242a; font-size: 36px; line-height: 36px; font-weight: 300; transform: translateY(-2px); }
  .inherit-detail-title { grid-column: 2; min-width: 0; overflow: hidden; color: #20242a; font-size: 14px; line-height: 20px; font-weight: 700; text-align: center; text-overflow: ellipsis; white-space: nowrap; }
  .inherit-group-summary { overflow: hidden; margin-top: 1px; color: #626873; font-size: 12px; line-height: 17px; font-weight: 400; text-overflow: ellipsis; white-space: nowrap; }
  .inherit-detail-filters { flex-shrink: 0; padding: 0 12px 10px; border-bottom: 1px solid #e5e7eb; box-sizing: border-box; background: #fff; }
  .inherit-message-search { width: 100%; height: 32px; margin-left: 0; border-color: transparent; background: #f6f7f8; }
  .inherit-message-list { display: block; min-height: 0; flex: 1; width: 100%; padding: 14px 16px 30px; box-sizing: border-box; background: #fff; overflow-x: hidden; }
  .inherit-load-more, .inherit-message-error { padding: 8px; color: #7a8494; font-size: 13px; line-height: 20px; text-align: center; }
  .inherit-message-error { margin-bottom: 10px; border-radius: 5px; background: #fff8e8; color: #8a5a00; }
  .inherit-retry { margin-left: 10px; display: inline; color: #2868c7; }
  .inherit-message-row { display: block; width: 100%; min-width: 0; margin: 0 0 26px; box-sizing: border-box; color: #1f2329; }
  .inherit-message-sender { margin-bottom: 7px; color: #60656f; font-size: 13px; line-height: 20px; }
  .inherit-message-time { margin-left: 14px; color: #6f7480; }
  .inherit-message-content { width: 100%; max-width: 100%; min-width: 0; overflow: hidden; box-sizing: border-box; word-break: break-word; }
  .inherit-open-message { display: inline-block; max-width: 100%; box-sizing: border-box; overflow: hidden; }
  .inherit-open-message * { max-width: 100%; box-sizing: border-box; }
  .inherit-message-fallback, .inherit-open-message-fallback { color: #171a1f; font-size: 16px; line-height: 24px; white-space: pre-wrap; }
  .inherit-spinning { animation: inherit-spin .8s linear infinite; }
  @keyframes inherit-spin { to { transform: rotate(360deg); } }
`;

export const WeComConversationInheritOpenDataFrame: React.FC<Props> = ({
  conversation,
  conversations,
  selectedConversation,
  activeTab,
  keyword,
  loading,
  listLoading,
  listRefreshing,
  messagesLoading,
  contextError,
  messageError,
  messages,
  hasMore,
  onRefresh,
  onTabChange,
  onKeywordChange,
  onConversationSelect,
  onBack,
  onLoadMore,
  onRetryMessages,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<OpenDataFrameInstance | null>(null);
  const messageScrollContextRef = useRef<ScrollViewContextInstance | null>(null);
  const latestDataRef = useRef<FrameData | null>(null);
  const pendingScrollToBottomRef = useRef(false);
  const previousSelectedConversationIdRef = useRef('');
  const actionsRef = useRef({
    onRefresh,
    onTabChange,
    onKeywordChange,
    onConversationSelect,
    onBack,
    onLoadMore,
    onRetryMessages,
  });
  const [frameFailed, setFrameFailed] = useState(false);

  useEffect(() => {
    actionsRef.current = {
      onRefresh,
      onTabChange,
      onKeywordChange,
      onConversationSelect,
      onBack,
      onLoadMore,
      onRetryMessages,
    };
  }, [onBack, onConversationSelect, onKeywordChange, onLoadMore, onRefresh, onRetryMessages, onTabChange]);

  const frameData = useMemo<FrameData>(() => {
    const headerOpenId = getWeComConversationOpenDataId(conversation);
    const externalCorpDisplay = getWeComExternalCorpDisplay(conversation);
    const headerName = conversation
      ? stripWeComExternalCorpSuffix(
        getWeComConversationFallbackName(conversation),
        externalCorpDisplay.label,
      )
      : '未选择员工';
    const isGroupContext = conversation?.type === 'group';
    const selectedOpenId = selectedConversation?.type === 'group'
      ? String(selectedConversation.wecom_room_id || '')
      : String(selectedConversation?.employee_wecom_userid || '');
    const selectedNameType: 'userName' | 'chatName' = selectedConversation?.type === 'group'
      ? 'chatName'
      : 'userName';

    return {
      mode: selectedConversation ? 'detail' : 'list',
      loading,
      listLoading,
      listRefreshing,
      messagesLoading,
      contextReady: Boolean(conversation),
      contextError,
      activeTab,
      keyword,
      searchPlaceholder: activeTab === 'single' ? '员工' : '群聊',
      headerOpenId,
      headerNameType: getWeComConversationNameType(headerOpenId),
      headerAvatarType: isGroupContext ? 'userAvatar' : 'externalUserAvatar',
      headerShowAvatarOpenData: Boolean(headerOpenId && !isGroupContext),
      headerAvatarImage: isGroupContext ? GROUP_CHAT_AVATAR_SRC : '',
      headerAvatarInitial: headerName.charAt(0) || '人',
      headerName,
      headerCompany: externalCorpDisplay.label,
      canRefresh: Boolean(conversation?.type === 'single' && conversation.external_userid && !listRefreshing),
      conversations: conversations.map<FrameConversation>((item) => {
        const openid = item.type === 'group'
          ? String(item.wecom_room_id || '')
          : String(item.employee_wecom_userid || '');
        return {
          id: String(item.id),
          rowClass: 'inherit-conversation-row',
          openid,
          name: item.name || (item.type === 'group' ? '客户群' : '未知员工'),
          nameType: item.type === 'group' ? 'chatName' : 'userName',
          avatarType: 'userAvatar',
          showAvatarOpenData: Boolean(item.type === 'single' && openid),
          avatarImage: item.type === 'group' ? GROUP_CHAT_AVATAR_SRC : '',
          avatarInitial: (item.name || '?').charAt(0),
          countText: `[今日${item.today_count}条] [总数${item.message_count}条] ${item.last_message || ''}`,
          lastMessageTime: item.last_message_time ? formatRecentMessageTime(item.last_message_time) : '',
        };
      }),
      selectedName: selectedConversation?.name || '',
      selectedOpenId,
      selectedNameType,
      selectedUseOpenData: Boolean(selectedOpenId),
      selectedGroupOwnerOpenId: selectedConversation?.type === 'group' && conversation?.type === 'group'
        ? String(conversation.group_creator || '')
        : '',
      selectedGroupOwnerName: selectedConversation?.type === 'group' && conversation?.type === 'group'
        ? String(conversation.group_creator_name || conversation.group_creator || '')
        : '',
      selectedGroupMemberText: selectedConversation?.type === 'group'
        && selectedConversation.group_member_count != null
        ? `${selectedConversation.group_member_count}人`
        : '',
      selectedShowGroupSummary: Boolean(
        selectedConversation?.type === 'group'
        && (
          (conversation?.type === 'group' && (conversation.group_creator || conversation.group_creator_name))
          || selectedConversation.group_member_count != null
        )
      ),
      canGoBack: Boolean(selectedConversation && conversation?.type !== 'group'),
      messages: messages.map(toWeComConversationDisplayMessage),
      hasMore,
      messageError,
    };
  }, [
    activeTab,
    contextError,
    conversation,
    conversations,
    hasMore,
    keyword,
    listLoading,
    listRefreshing,
    loading,
    messageError,
    messages,
    messagesLoading,
    selectedConversation,
  ]);
  latestDataRef.current = frameData;
  const currentSelectedConversationId = String(selectedConversation?.id || '');
  if (currentSelectedConversationId !== previousSelectedConversationIdRef.current) {
    previousSelectedConversationIdRef.current = currentSelectedConversationId;
    pendingScrollToBottomRef.current = Boolean(currentSelectedConversationId);
    messageScrollContextRef.current = null;
  }

  useEffect(() => {
    let disposed = false;
    const ensureMessageScrollContext = async () => {
      if (disposed || !instanceRef.current || messageScrollContextRef.current) return;
      try {
        messageScrollContextRef.current = await createWeComScrollViewContext(
          instanceRef.current,
          'messageScroll',
        ) as ScrollViewContextInstance;
      } catch {
        messageScrollContextRef.current = null;
      }
    };
    const destroy = () => {
      disposeWeComOpenDataFrame(instanceRef.current);
      instanceRef.current = null;
      messageScrollContextRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = '';
    };

    const mount = async () => {
      if (!containerRef.current) return;
      instanceRef.current = await mountWeComOpenDataFrame({
          el: containerRef.current,
          template,
          style,
          data: latestDataRef.current || frameData,
          handleModal(event: { modalUrl?: string; modalSize?: { width?: number; height?: number } }) {
            return openWeComPreviewModal(event) ? false : true;
          },
          handleMounted() {
            window.setTimeout(ensureMessageScrollContext, 0);
          },
          handleUpdated() {
            window.setTimeout(async () => {
              if (latestDataRef.current?.mode !== 'detail') {
                messageScrollContextRef.current = null;
                return;
              }
              if (latestDataRef.current?.mode === 'detail') {
                await ensureMessageScrollContext();
              }
              if (
                pendingScrollToBottomRef.current
                && latestDataRef.current?.mode === 'detail'
                && latestDataRef.current.messages.length > 0
                && messageScrollContextRef.current
              ) {
                messageScrollContextRef.current.scrollTo?.({ top: 1_000_000_000 });
                pendingScrollToBottomRef.current = false;
              }
            }, 0);
            window.setTimeout(() => triggerWWOpenDataBind(), 0);
          },
          methods: {
            onRefresh() {
              actionsRef.current.onRefresh();
            },
            onTabChange(event: any) {
              const type = String(event?.currentTarget?.dataset?.type || event?.target?.dataset?.type || '');
              if (type === 'single' || type === 'group') actionsRef.current.onTabChange(type);
            },
            onSearchInput(event: any) {
              actionsRef.current.onKeywordChange(String(event?.detail?.value || ''));
            },
            onConversationClick(event: any) {
              const id = String(event?.currentTarget?.dataset?.id || event?.target?.dataset?.id || '');
              if (id) actionsRef.current.onConversationSelect(id);
            },
            onBack() {
              actionsRef.current.onBack();
            },
            onLoadMore() {
              if (pendingScrollToBottomRef.current) return;
              actionsRef.current.onLoadMore();
            },
            onRetryMessages() {
              actionsRef.current.onRetryMessages();
            },
            onOpenMessageError(event: any) {
              console.warn('[WeComConversationInheritOpenDataFrame] 会话消息渲染失败:', event?.detail || event);
            },
          },
        }, { isDisposed: () => disposed, stage: 'conversation-inherit' }) as OpenDataFrameInstance;
      if (!instanceRef.current && !disposed) {
        setFrameFailed(true);
      }
    };

    mount();
    return () => {
      disposed = true;
      destroy();
    };
  }, []);

  useEffect(() => {
    try {
      instanceRef.current?.setData?.(frameData);
    } catch {
      // 首次挂载尚未完成时，createOpenDataFrame 会读取最新数据。
    }
  }, [frameData]);

  if (frameFailed) {
    return (
      <Box sx={{ height: '100vh', display: 'grid', placeItems: 'start center', pt: '30vh', bgcolor: '#fff' }}>
        <Typography color="text.secondary" sx={{ fontSize: 14 }}>会话展示组件初始化失败，请刷新重试</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100vh',
        minWidth: 0,
        overflow: 'hidden',
        bgcolor: '#fff',
        '& > *': {
          width: '100% !important',
          maxWidth: 'none !important',
          height: '100% !important',
          minWidth: '0 !important',
        },
        '& iframe': {
          display: 'block',
          width: '100% !important',
          maxWidth: 'none !important',
          height: '100% !important',
          border: 0,
        },
      }}
    />
  );
};
